import React, { Dispatch, SetStateAction, useState } from 'react';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import BulkApplyButton from './BulkApplyButton';
import BulkSkipButton from './BulkSkipButton';
import ConflictingTagsMultiSelect from './ConflictsTable/ConflictingTagsMultiSelect';
import ConflictsTable from './ConflictsTable/ConflictsTable';
import { useConflictTableState } from './ConflictsTable/logic/useConflictTableState';
import { useGetDebouncedConflicts } from './ConflictsTable/logic/useGetDebouncedConflicts';

export interface ConflictsDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<TagGroupGq>>;
  project: ProjectGq;
  tagGroup: TagGroupGq;
  reload: () => Promise<any>;
}

const ConflictsDialog: React.FC<ConflictsDialogProps> = ({
  openDialog,
  setOpenDialog,
  project,
  tagGroup,
  reload,
}) => {
  const [amountPerPage, setAmountPerPage] = useState<number>(8);

  // Table state
  const tableState = useConflictTableState(
    {
      projectId: project.id,
    },
    {
      invertSelection: false,
      keywordIds: [],
      emptyIsAll: false,
    },
    undefined
  );

  const [localPage, setLocalPage] = useState<number>(0);
  // Table data
  const { conflicts, totalCount, page, query } = useGetDebouncedConflicts(
    tagGroup.id,
    amountPerPage,
    localPage,
    tableState,
    {
      pollInterval: 15000,
    }
  );

  const closeDialog = () => {
    setOpenDialog(null);
    setLocalPage(0);
  };

  const localReload = () => {
    const tableReloadPromise = query?.refetch() ?? Promise.resolve();
    reload();
    return tableReloadPromise;
  };

  const areConflictsSelected =
    Object.values(tableState.selectionState.keywordIds).length > 0 ||
    tableState.selectionState.invertSelection;

  const [bulkSelectedTag, setBulkSelectedTag] = useState<number>(null);

  return (
    <VoxelDialog
      open={openDialog}
      onClose={closeDialog}
      title={`Autotag conflicts`}
      minWClass='min-w-[50vw]'
      maxWClass='max-w-[100vw]'
      subtitle='Autotag conflicts occur when multiple tags text match with the same keyword. 
      Solve them either by applying one of the suggested tags or by selecting skip.'
    >
      <VoxelDialogContent classMinWidth='flex flex-col gap-4'>
        {areConflictsSelected ? (
          < div className=' flex flex-col gap-2'>
            <VoxelText variant={'title3'} color='dark'>
              Bulk apply tag to selected keywords (requires that the tag is among the suggested tags for that keyword)
            </VoxelText>
            <div className='bg-light-blue-50 p-3 rounded-lg'>
              <ConflictingTagsMultiSelect
                tagGroup={tagGroup}
                tableState={tableState}
                areConflictsSelected={areConflictsSelected}
                bulkSelectedTag={bulkSelectedTag}
                setBulkSelectedTag={setBulkSelectedTag}
              />
            </div>
          </div>
        )
          : (
            <VoxelText variant={'title3'} color='dark'>
              * Select keywords to solve many conflicts at once
            </VoxelText>
          )}
        <ConflictsTable
          tableState={tableState}
          conflicts={conflicts}
          tagGroup={tagGroup}
          reload={localReload}
          totalCount={totalCount}
          localPage={localPage}
          amountPerPage={amountPerPage}
          setLocalPage={setLocalPage}
          areConflictsSelected={areConflictsSelected}
          bulkSelectedTag={bulkSelectedTag}
          setBulkSelectedTag={setBulkSelectedTag}
        />

      </VoxelDialogContent>
    </VoxelDialog >
  );
};

export default ConflictsDialog;
